import type { PlatformServices } from "../platform/PlatformServices";
import type { SyncEngine } from "./SyncEngine";

/** Timer functions of the triggers (injected for tests). */
export interface TriggerTimers {
  setInterval(callback: () => void, intervalMs: number): number;
  clearInterval(handle: number): void;
}

/** Foreground synchronization interval (spec 8.1.3). */
export const foregroundSyncIntervalMs = 60_000;

/**
 * Starts the triggers of the synchronization (spec 8.1.3, 8.3): app start, regained connectivity,
 * the application coming to the foreground and an interval while in the foreground. The platform
 * decides what these mean: `online` and `visibilitychange` in the browser, the `Network` plugin and
 * `appStateChange` in the Capacitor apps (T-082). Background Sync (Android/desktop PWA) is added by
 * `startBackgroundSyncSupport` (T-073).
 *
 * @param engine - The sync engine.
 * @param platform - Platform services.
 * @param timers - Interval functions.
 * @param intervalMs - Interval in the foreground.
 * @returns A function that stops all triggers.
 */
export function startSyncTriggers(
  engine: Pick<SyncEngine, "syncNow">,
  platform: Pick<PlatformServices, "onNetworkChange" | "onResume" | "isInForeground">,
  timers: TriggerTimers,
  intervalMs: number = foregroundSyncIntervalMs,
): () => void {
  const trigger = (): void => {
    void engine.syncNow();
  };
  const interval = timers.setInterval(() => {
    if (platform.isInForeground()) {
      trigger();
    }
  }, intervalMs);
  const stopNetwork = platform.onNetworkChange((online) => {
    if (online) {
      trigger();
    }
  });
  const stopResume = platform.onResume(trigger);
  trigger();

  const stop = (): void => {
    timers.clearInterval(interval);
    stopNetwork();
    stopResume();
  };

  return stop;
}
